import { useCallback, useEffect, useRef, useState } from 'react';
import { AgTable, Pagination } from '@navi/web-ui/lib/components';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { DropDownPosition } from '@cp/src/pages/AmeyoUtility/types';
import { getBankStatementInfo } from './bankStatementColDefs';
import { CloseIcon, SearchIcon } from '@navi/web-ui/lib/icons';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { logError } from '@cp/src/utils/ApiHelper';
import { useNavigate, useParams } from 'react-router-dom';
import { getBankStatementResponse } from '../actions';
import SearchBarInput from '@navi/web-ui/lib/components/SearchBarInput';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from '@cp/src/components/TableLoader/TableLoader';
import BankStatementWorkerWrapper from './workerInitializer';
import styles from '../index.module.scss';
import BankStatementNotAvailable from './BankStatementNotAvailable';
import SearchQueryNotFound from './SearchQueryNotFound';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DOCUMENTS, MIN_SEARCH_LENGTH } from '../constants';
import { noop } from '@navi/web-ui/lib/utils/common';
import { BankStatementWorker } from './bankStatementWorker';
import * as Comlink from 'comlink';
import NumberDropDown from './NumberDropDown';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { IBankStatementParams, IBankStatementResponse } from '../type';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBankStatementData,
  setBankStatementNumbers,
  setBankStatementRowData
} from '../reducer/bankStatementSlice';
import { RootState } from '@cp/src/store';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { createKey } from '@cp/src/utils/CaseDetail.utils';
import { PAUSE_CASE_LH_VISIBILITY_ROLES } from '@cp/src/constants/Common.constants';

const BankStatement = ({
  isOnModal,
  setIsExpandedView
}: {
  isOnModal: boolean;
  setIsExpandedView: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] = useState(() => {
    const { [DOCUMENTS]: params } = readQueryParams();
    return params;
  });

  const [totalCount, setTotalCount] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isBankStatementAvailable, setIsBankStatementAvailable] = useState(true);
  const bankStatementInstance = useRef<Comlink.Remote<BankStatementWorker> | null>(null);

  const data = useSelector((state: RootState) => state.bankStatementSlice.bankStatementData);
  const rowData = useSelector((state: RootState) => state.bankStatementSlice.bankStatementRowData);

  const updateAndNavigate = (updatedParams: IBankStatementParams) => {
    setQueryParams((prevQueryParams: IBankStatementParams) => {
      const newParams = { ...prevQueryParams, ...updatedParams };
      const url = createQueryParams({
        [DOCUMENTS]: newParams
      });
      navigate(url);
      return newParams;
    });
  };

  const setInitialQueryParams = (params: IBankStatementParams) => {
    const queryParams: IBankStatementParams = { ...params };
    if (!queryParams.pageSize) {
      queryParams['pageSize'] = String(DEFAULT_PAGE_SIZE);
    }
    if (!queryParams.pageNo) {
      queryParams['pageNo'] = String(DEFAULT_PAGE_NUMBER);
    }
    updateAndNavigate(queryParams);
  };

  const isSnoozed = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );
  const userRole = useSelector((state: RootState) => state.common?.userData?.roles);
  const hasVisibilityRole = userRole?.some(role => PAUSE_CASE_LH_VISIBILITY_ROLES.includes(role));

  useEffect(() => {
    if (!isSnoozed || (isSnoozed && !hasVisibilityRole)) {
      getBankStatementResponse(
        loanId,
        customerId,
        caseReferenceId,
        bankStatementErrorCallback
      ).then(response => {
        getBankStatement(response);
      });
    }
    setInitialQueryParams(queryParams);
    return () => {
      bankStatementInstance.current?.resetData();
    };
  }, []);

  const transformRowData = () => {
    return data?.map((item: GenericObject) => {
      return {
        transactionDateTime: item?.timestamp,
        transactionNarration: {
          narration: item?.transactionNarration.rawText,
          filteredNumber: queryParams?.filterNumber,
          searchValue: queryParams?.searchValue,
          searchStart: item?.searchStart,
          filterStart: item?.transactionNarration?.annotatedSpan,
          isOnModal: isOnModal,
          setIsExpandedView: setIsExpandedView
        },
        transactionType: item?.amount.type,
        transactionAmount: item?.amount.value,
        accountBalance: item?.balance
      };
    });
  };
  const bankStatementErrorCallback = () => {
    setIsBankStatementAvailable(false);
  };

  useEffect(() => {
    getPaginatedData(
      Number(queryParams?.pageNo),
      Number(queryParams?.pageSize),
      queryParams?.searchValue,
      queryParams?.filterNumber,
      queryParams?.identifier
    );
  }, [queryParams]);

  useEffect(() => {
    data && dispatch(setBankStatementRowData([...transformRowData()]));
  }, [data]);

  const getPaginatedData = async (
    pageNumber: number,
    pageSize: number,
    searchValue?: string,
    filteredNo?: string,
    identifier?: string
  ) => {
    try {
      setIsTableLoading(true);
      const pageDataAndLength = await bankStatementInstance?.current?.getPagingData(
        pageNumber || DEFAULT_PAGE_NUMBER,
        pageSize || DEFAULT_PAGE_SIZE,
        searchValue || '',
        filteredNo,
        identifier
      );
      dispatch(setBankStatementData(pageDataAndLength?.data || []));
      dispatch(setBankStatementNumbers(pageDataAndLength?.aggregationNumbers || []));
      setTotalCount(pageDataAndLength?.length);
      setIsTableLoading(false);
    } catch {
      setIsTableLoading(false);
      logError('Error in fetching paginated data');
    }
  };

  const getBankStatement = async (response: IBankStatementResponse) => {
    try {
      const instanceBankStatement = await new BankStatementWorkerWrapper();
      setIsTableLoading(true);
      await instanceBankStatement.fetchBankStatements(response);
      bankStatementInstance.current = instanceBankStatement;
      await getPaginatedData(
        queryParams.pageNo,
        queryParams.pageSize,
        queryParams.searchValue,
        queryParams.filterNumber,
        queryParams.identifier
      );
      setIsTableLoading(false);
    } catch (error) {
      logError(error);
      setIsTableLoading(false);
    }
  };

  const handleSearchChange = (searchValue: string) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_SEARCH);

    if (searchValue.length == 0) {
      updateAndNavigate({ searchValue: undefined, pageNo: String(DEFAULT_PAGE_NUMBER) });
    }

    if (searchValue.length < MIN_SEARCH_LENGTH) {
      return;
    }
    updateAndNavigate({ searchValue, pageNo: String(DEFAULT_PAGE_NUMBER) });
  };

  const handleFilterChange = (filteredNumber: string, identifier: string) => {
    updateAndNavigate({
      filterNumber: filteredNumber,
      identifier: identifier,
      pageNo: String(DEFAULT_PAGE_NUMBER)
    });
  };

  const handleSearchValueReset = () => {
    updateAndNavigate({ searchValue: '' });
  };

  const handlePageChange = (pageNumber: number) => {
    updateAndNavigate({ pageNo: String(pageNumber) });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateAndNavigate({ pageSize: String(pageSize), pageNo: String(DEFAULT_PAGE_NUMBER) });
  };
  const onCloseModal = () => {
    const { [DOCUMENTS]: params } = readQueryParams();
    updateAndNavigate(params);
  };

  return (
    <>
      {!isBankStatementAvailable ? (
        <BankStatementNotAvailable />
      ) : (
        <>
          <div className={cx({ 'pt-1.5 pl-4 pr-4': !isOnModal, 'pt-2.5': isOnModal })}>
            <div className={'mb-2 flex justify-start items-center'}>
              {isOnModal && (
                <Typography variant={'h3'} className="mr-6 w-[133px]">
                  Bank Statement
                </Typography>
              )}
              <div
                className={cx('flex !justify-between items-center w-[100%]', {
                  'mr-[-45px]': !isOnModal
                })}
              >
                <SearchBarInput
                  placeholder="Search here..."
                  debounceDelay={300}
                  inputSize={'medium'}
                  containerClassName={'w-[200px] '}
                  value={queryParams?.searchValue || ''}
                  onSearchChange={handleSearchChange}
                  LeftInputAdornment={<SearchIcon style={{ cursor: 'text' }} />}
                  RightInputAdornment={
                    queryParams?.searchValue?.length > 0 ? (
                      <Button variant="text" onClick={handleSearchValueReset}>
                        <CloseIcon />
                      </Button>
                    ) : null
                  }
                />
                <NumberDropDown
                  queryParams={queryParams}
                  handleFilterChange={handleFilterChange}
                  isOnModal={isOnModal}
                  onCloseModal={onCloseModal}
                  setIsExpandedView={setIsExpandedView}
                />
              </div>
            </div>
            <div className={'w-full'} id="bankStatementTable">
              <SuspenseLoader loading={isTableLoading} fallBack={<TableLoader numberOfLines={6} />}>
                <div className={cx(styles.table, { '!h-[85%]': isOnModal })}>
                  <AgTable
                    className={'customAgTableStyles'}
                    defaultColDef={{
                      suppressMovable: true,
                      wrapText: true
                    }}
                    gridOptions={{ suppressCellSelection: true }}
                    domLayout="autoHeight"
                    sizeColumnsToFit
                    rowData={rowData}
                    columnDefs={getBankStatementInfo}
                    theme={'alpine'}
                    noRowsOverlayComponent={() => <SearchQueryNotFound />}
                    paginationWrapperClasses={'paginationWrapperClasses'}
                    getRowHeight={() => {
                      return 75;
                    }}
                    animateRows={true}
                    loadingOverlayComponent={noop}
                    PaginationComponent={
                      <Pagination
                        totalCount={totalCount}
                        currentPage={Number(queryParams?.pageNo) || DEFAULT_PAGE_NUMBER}
                        pageSize={Number(queryParams?.pageSize) || DEFAULT_PAGE_SIZE}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageNumberDropDownPosition={DropDownPosition.TOP}
                        hidePageDetails={true}
                      />
                    }
                  />
                </div>
              </SuspenseLoader>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BankStatement;
